import cx from 'classnames'
import { addDisplayName, addEffect, addProps, addRef, flowMax } from 'ad-hok'
import { FC } from 'react'
import lottie, { AnimationItem } from 'lottie-web'
import { createUseStyles } from 'react-jss'
import { addEffectOnMount } from 'ad-hok-utils'
import { typedAs } from 'shared/src/utils/typedAs'

import animationData from '../assets/loading-animation.json'
import { constrainWidth } from '../utils/layout'

const useStyles = createUseStyles({
  container: {
    height: '100%',
    width: '100%',
    opacity: '100%',
    transitionProperty: 'opacity',
    transitionDuration: '0.25s',
  },
  fadingOut: {
    opacity: '0%',
  },
})

type State = 'loading' | 'fadingOut'

interface Props {
  state: State
  onComplete?: () => void
  className?: string
}

export const Loading: FC<Props> = flowMax(
  addDisplayName('Loading'),
  constrainWidth({ fillHeight: true, positionRelative: true }),
  addRef('divRef', typedAs<HTMLDivElement | null>(null)),
  addRef('animationRef', typedAs<AnimationItem | null>(null)),
  addEffect(
    ({ state, divRef, onComplete }) =>
      () => {
        if (state === 'fadingOut' && divRef && divRef.current) {
          setTimeout(() => {
            onComplete && onComplete()
          }, 250)
        }
      },
    ['state', 'divRef']
  ),
  addEffectOnMount(({ divRef, animationRef }) => () => {
    const animation = lottie.loadAnimation({
      container: divRef.current!,
      renderer: 'svg',
      loop: true,
      animationData,
    })

    animation.play()
    animationRef.current = animation
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({ divRef, state, classes, className }) => (
    <div
      ref={divRef}
      className={cx(
        classes.container,
        state === 'fadingOut' && classes.fadingOut,
        className
      )}
    />
  )
)
